import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import { AxiosResponse } from 'axios';

import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import { ApiUrl, Constants } from '@/utils/constants';
import url from '@/utils/config';

import alertState from '@/states/alertState';
import { CampaignInfoState } from '@/states/campaignInfoState';

import { CampaignInfoResponse } from '@/types/api/mypage/campaignInfo';
import useSetLoading from '../useSetLoading';

/** キャンペーン情報取得APIのURL */
const apiUrl = ApiUrl.Read.CAMPAIGN_INFO;

/**
 * キャンペーン情報取得APIの呼び出しフック。
 * 現在契約に関連するキャンペーン情報を取得し、画面表示用stateへ保存する。
 *
 * @returns execute - API実行関数
 */
const useCallAPIGetCampaignInfo = () => {
  const { startLoading, finishLoading } = useSetLoading();
  const setCampaignInfo = useSetRecoilState(CampaignInfoState);
  const setAlert = useSetRecoilState(alertState);

  const execute = useCallback(async () => {
    try {
      startLoading(ApiUrl.Read.CAMPAIGN_INFO);
      const headers = await getSigV4HeadersForGet(apiUrl);
      const response: AxiosResponse<CampaignInfoResponse, any> = await api.get(apiUrl, headers);
      // キャンペーン一覧・詳細で使い回せるよう、取得結果をstateへ保持する。
      setCampaignInfo(response.data);
    } catch (error: any) {
      if (isExpiredToken(error)) {
        // 認証切れ時は再ログインへ誘導する。
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }
      // 取得失敗は共通アラートで利用者へ通知する。
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(ApiUrl.Read.CAMPAIGN_INFO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute };
};

export default useCallAPIGetCampaignInfo;
